import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteToken } from "../../../infrastructure/store/authSlice";
import { logoutService } from "../../../infrastructure/services/AuthService";
import { clearSessionData } from "../../../infrastructure/utils/clearSessionData";
import {
  LangContext,
  UiTextsContext,
  UserDataContext,
  RegisterUserContext,
} from "../../contexts/Contexts";
import { basePath } from "../../routes/routes";

// Cierre de sesión automático por inactividad.
// El conteo se reinicia con cualquier interacción del usuario (mouse, teclado,
// scroll, touch) y persiste en localStorage para sobrevivir recargas y
// sincronizarse entre pestañas del mismo navegador.
const INACTIVITY_LIMIT_MS = 3 * 60 * 60 * 1000; // 3 horas
const CHECK_INTERVAL_MS = 60 * 1000; // revisa cada minuto
const ACTIVITY_THROTTLE_MS = 5 * 1000; // no escribe en localStorage más de 1 vez cada 5s
const LAST_ACTIVITY_KEY = "lastActivityAt";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "wheel"];

const SessionInactivityWatcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useSelector((s) => s.authToken);

  const { setLang } = useContext(LangContext);
  const { setUserData } = useContext(UserDataContext);
  const { setRegister } = useContext(RegisterUserContext);
  const { setTexts } = useContext(UiTextsContext);

  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (!userId) return;

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };

    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      markActivity();
    }

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, markActivity, { passive: true })
    );

    const forceLogout = async () => {
      try {
        // Best-effort: si el backend no acepta el id (hoy espera un GUID de
        // sesión/usuario) igual se limpia la sesión local.
        await logoutService({ userId: String(userId) });
      } catch (error) {
        console.error("Error notificando logout automático al backend:", error);
      }
      dispatch(deleteToken());
      clearSessionData();
      setUserData({});
      setRegister(null);
      setTexts({});
      setLang("en");
      navigate(basePath);
    };

    const intervalId = setInterval(() => {
      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();
      if (Date.now() - lastActivity >= INACTIVITY_LIMIT_MS) {
        clearInterval(intervalId);
        forceLogout();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity));
      clearInterval(intervalId);
    };
  }, [userId, dispatch, navigate, setUserData, setRegister, setTexts, setLang]);

  return null;
};

export default SessionInactivityWatcher;
